import {
  ClinicVisitTypeAvailabilitySection,
  ClinicVisitTypeDto
} from "@/components/blocks/sections/ClinicVisitTypeAvailabilitySection"
import { H1 } from "@/components/ui-library/typography/Headings"
import OpenNewTabIcon from "@/layout/icons/OpenNewTabIcon"
import { useConfigStore } from "@/store/config-store"
import { BlokProps } from "@helpers/StoryblokHelpers"
import InfoIcon from "@layout/icons/InfoIcon"
import MapIcon from "@layout/icons/MapIcon"
import TimeIcon from "@layout/icons/TimeIcon"
import { ClinicPageHeroStoryblok } from "@storyblok-types"
import { SbBlokData, storyblokEditable } from "@storyblok/react/rsc"
import Col from "@ui-library/grid/Col"
import Row from "@ui-library/grid/Row"
import GridWrapper from "@ui-library/grid/Wrapper"
import { Paragraph } from "@ui-library/typography/Body"
import { Label } from "@ui-library/typography/Labels"
import { cn } from "@utils/utils"
import Image from "next/image"
import Link from "next/link"

export const ClinicPageHero = ({
  blok,
  clinicId: clinicIdProp
}: BlokProps<ClinicPageHeroStoryblok> & { clinicId?: string }) => {
  const showOther = blok.show_other_information
  const strings = useConfigStore.getState().strings
  // Prefer the clinic id passed down from the clinic page (server, per-request); fall back to the
  // config store for other render paths (e.g. preview). Reading the store in a server component is
  // unreliable across client-side navigation, so the prop is the source of truth on the clinic route.
  const clinicId = clinicIdProp ?? (useConfigStore.getState().config?.platform_clinic_id as string | undefined)
  const visitTypeDto = blok?.clinic_visit_types as ClinicVisitTypeDto[] | undefined

  return (
    <GridWrapper className="row flex flex-col flex-wrap lg:px-4" {...storyblokEditable(blok as SbBlokData)}>
      <Row justifyContent="center" justifyContentMobile="center" className="order-1">
        <Col grid={{ xs: 12 }} className="text-center">
          <H1 className="display-header mb-0">{blok.heading}</H1>
        </Col>
      </Row>
      <Row justifyContent="center" justifyContentMobile="center" className="order-3 lg:order-2">
        <Col grid={{ xs: 12, lg: 10, xl: 8 }} className="text-left lg:text-center">
          <Paragraph size="medium">{blok.intro}</Paragraph>
        </Col>
      </Row>
      <figure className="relative order-2 mx-auto mb-4 aspect-[1.6] w-full max-w-footer overflow-hidden rounded-2xl lg:order-3 lg:mb-0 lg:mt-8 lg:aspect-[3] xl:aspect-[3.6]">
        {blok.image.filename && (
          <Image
            src={blok.image.filename || ""}
            fill
            priority
            sizes="100vw"
            alt={blok.image.alt || ""}
            className="object-cover"
          />
        )}
      </figure>

      {blok?.show_open_timeslots &&
        clinicId &&
        ((Array.isArray(visitTypeDto) && visitTypeDto.length > 0) ||
          (!Array.isArray(visitTypeDto) && visitTypeDto)) && (
          <ClinicVisitTypeAvailabilitySection clinicId={clinicId} visitTypes={visitTypeDto} showOther={showOther} />
        )}
      <Row
        className={cn(
          blok.show_open_timeslots ? "order-5 my-6 px-4 lg:my-0 lg:mt-24" : "order-4 my-4 px-4 lg:my-0",
          { "lg:mb-[-6rem] lg:translate-y-[-6rem]": showOther },
          { "lg:mb-[-4rem] lg:translate-y-[-4rem]": !showOther }
        )}
        justifyContent="center"
        justifyContentMobile="center"
      >
        <InfoBarColumn>
          <Label size="small" asChild className="mb-2 flex items-center justify-start not-italic">
            <em>
              <TimeIcon className="mr-3 max-h-[1.5rem] max-w-[1.5rem]" />
              <span>{blok.open_hours_label ? blok.open_hours_label : "Öppettider"}</span>
            </em>
          </Label>
          <Paragraph size="small" className="inline-block pl-9">
            {blok.open_hours_text}
          </Paragraph>
        </InfoBarColumn>

        <InfoBarColumn>
          <Label size="small" asChild className="mb-2 flex items-center justify-start not-italic">
            <em>
              <MapIcon className="mr-3 max-h-[1.5rem] max-w-[1.5rem]" />
              <span>{blok.location_label ? blok.location_label : "Adress"}</span>
            </em>
          </Label>

          <Paragraph size="small" asChild className="inline-block pl-9 not-italic">
            <address>
              <Link
                href={blok.location_url ? blok.location_url : "#"}
                target={blok.location_url ? "_blank" : ""}
                rel={blok.location_url ? "noopener noreferer" : ""}
                className="flex items-center"
              >
                {blok.location_text}
                {blok.location_url && (
                  <OpenNewTabIcon
                    className="ml-2 inline-block"
                    width={16}
                    height={16}
                    aria-label={strings?.links.open_in_new_tab}
                  />
                )}
              </Link>
            </address>
          </Paragraph>
        </InfoBarColumn>

        {blok.show_other_information && (
          <InfoBarColumn>
            <Label size="small" asChild>
              <em className="mb-2 flex items-center justify-start not-italic">
                <InfoIcon className="mr-3 max-h-[1.5rem] max-w-[1.5rem]" />
                <span>{blok.other_information_label ? blok.other_information_label : "Övrig information"}</span>
              </em>
            </Label>
            {blok.other_information_text && (
              <Paragraph size="small" className="inline-block pl-9">
                {blok.other_information_text}
              </Paragraph>
            )}
          </InfoBarColumn>
        )}
      </Row>
    </GridWrapper>
  )
}

export default ClinicPageHero

const InfoBarColumn = ({ children }: { children: React.ReactNode }) => {
  return (
    <Col
      grid={{ xs: 12, lg: 4 }}
      className="my-0 bg-primary-70 first:rounded-t-2xl last:rounded-b-2xl lg:first:rounded-l-2xl lg:first:rounded-tr-none lg:last:rounded-r-2xl lg:last:rounded-bl-none"
    >
      <div className="w-full max-w-full p-4 text-on-primary-70 lg:p-8">{children}</div>
    </Col>
  )
}
